import React, { useEffect, useState } from 'react';
import { createManager, getLeftMenus, getPortlets } from '../../api/managerApi';
import { useNavigate } from 'react-router-dom';
import {
  MANAGER_MENU_ITEMS,
  managerFieldLabels,
  managerLFields,
  managerMFields,
  managerPortalPageFields,
  managerBooleanRadioFields,
  managerFileFields,
  managerRadioOptionsMap,
  managerIsRadioChecked,
  managerInitialForm,
  setManagerMenuItemsFromDb,
  setPortletItemsFromDb,
  serializeManagerSelectedMenus,
  serializeManagerSelectedPortlet,
} from '../../utils/managerUtils';
import MenuListSelector from '../../components/manager/MenuListSelector';
import PortletLayoutSelector from '../../components/manager/PortletLayoutSelector';
import '../../css/manager.css';

const booleanRadioFields = managerBooleanRadioFields;
const fileFields = managerFileFields;
const radioOptionsMap = managerRadioOptionsMap;
const isRadioChecked = managerIsRadioChecked;

const ManagerCreate = () => {
  const [form, setForm] = useState(managerInitialForm);
  const [files, setFiles] = useState({});
  const [selectedLayout, setSelectedLayout] = useState(6);
  const [zoneData, setZoneData] = useState({});
  const [selectedMenus, setSelectedMenus] = useState([]);
  const [availableMenus, setAvailableMenus] = useState(MANAGER_MENU_ITEMS);
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([getLeftMenus(), getPortlets()])
      .then(([menuRows, portletRows]) => {
        setAvailableMenus(setManagerMenuItemsFromDb(menuRows));
        setPortletItemsFromDb(portletRows);
      })
      .catch((err) => {
        console.error('좌측메뉴/포틀릿 목록 조회 실패:', err);
        setAvailableMenus(MANAGER_MENU_ITEMS);
      });
  }, []);

  useEffect(() => {
    setForm(prev => ({ ...prev, P_PortletTypeNum: selectedLayout }));
  }, [selectedLayout]);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      P_SelectPortlet: serializeManagerSelectedPortlet(zoneData),
    }));
  }, [zoneData]);

  useEffect(() => {
    setForm(prev => ({
      ...prev,
      P_SelectMenus: serializeManagerSelectedMenus(selectedMenus),
    }));
  }, [selectedMenus]);

  const handleFileChange = e => {
    const { name, files: selected } = e.target;
    setFiles(prev => ({ ...prev, [name]: selected[0] }));
  };

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm({
      ...form,
      [name]:
        type === 'radio'
          ? (booleanRadioFields.includes(name) ? value === 'true' : value)
          : type === 'checkbox'
            ? checked
            : value
    });
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();
    const formData = new FormData();
    Object.entries(form).forEach(([k, v]) => {
      if (!fileFields.includes(k)) formData.append(k, v ?? '');
    });
    fileFields.forEach(field => {
      if (files[field]) formData.append(field, files[field]);
    });
    await createManager(formData);
    window.dispatchEvent(new CustomEvent('managerDataChanged'));
    navigate('/manager');
  };

  return (
    <div className="container-fluid view-manager">
      <div className="row">
        <div className="col-12">
          {/* 상단 버튼 영역 */}
          <div className="manager-header-fixed mb-3 d-flex justify-content-between align-items-center">
            <div className="fw-bold text-dark">신규회사등록</div>
            <div className="d-flex gap-2">
              <button className="btn btn-primary" onClick={handleSubmit}>등록</button>
              <button className="btn btn-outline-secondary" onClick={() => navigate('/manager')}>목록</button>
            </div>
          </div>

          {/* M_ 필드 정보 */}
          <div className="manager-body manager-m mb-4 mt-3">
            <h6 className="fw-bold mb-3">회사기본 정보</h6>
            <div className="row">
              {managerMFields.map(field => (
                <div className="col-md-6 mb-2" key={field}>
                  <label className="form-label">{managerFieldLabels[field]}</label>
                  {radioOptionsMap[field] ? (
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {radioOptionsMap[field].map(opt => (
                        <label key={opt.value} className="form-check-label d-flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={field}
                            value={opt.value}
                            checked={isRadioChecked(field, opt.value, form[field])}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : fileFields.includes(field) ? (
                    <input
                      type="file"
                      name={field}
                      onChange={handleFileChange}
                      className="form-control"
                      accept="image/*"
                    />
                  ) : (
                    <input
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="form-control"
                      type={field.includes('PW') ? 'password' : (field.includes('Port') || field.includes('Size')) ? 'number' : 'text'}
                      required={['M_ComCode','M_ComName','M_Domain','M_DBName','M_DBHost','M_DBUser','M_DBPW'].includes(field)}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* L_ 필드 정보 */}
          <div className="manager-body manager-l mb-4">
            <h6 className="fw-bold mb-3">로그인 페이지 정보</h6>
            <div className="row">
              {managerLFields.map(field => (
                <div className="col-md-6 mb-2" key={field}>
                  <label className="form-label">{managerFieldLabels[field]}</label>
                  {radioOptionsMap[field] ? (
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {radioOptionsMap[field].map(opt => (
                        <label key={opt.value} className="form-check-label d-flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={field}
                            value={opt.value}
                            checked={isRadioChecked(field, opt.value, form[field])}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : fileFields.includes(field) ? (
                    <input
                      type="file"
                      name={field}
                      onChange={handleFileChange}
                      className="form-control"
                      accept="image/*"
                    />
                  ) : (
                    <input
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="form-control"
                      type="text"
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* P_ 필드 정보 */}
          <div className="manager-body manager-p mb-4">
            <h6 className="fw-bold mb-3">포탈 페이지 정보</h6>
            <div className="row">
              {managerPortalPageFields.map(field => (
                <div className="col-md-6 mb-2" key={field}>
                  <label className="form-label">{managerFieldLabels[field]}</label>
                  {radioOptionsMap[field] ? (
                    <div className="d-flex gap-3 align-items-center pt-1">
                      {radioOptionsMap[field].map(opt => (
                        <label key={opt.value} className="form-check-label d-flex align-items-center gap-1">
                          <input
                            type="radio"
                            name={field}
                            value={opt.value}
                            checked={isRadioChecked(field, opt.value, form[field])}
                            onChange={handleChange}
                            className="form-check-input"
                          />
                          {opt.label}
                        </label>
                      ))}
                    </div>
                  ) : fileFields.includes(field) ? (
                    <input
                      type="file"
                      name={field}
                      onChange={handleFileChange}
                      className="form-control"
                      accept="image/*"
                    />
                  ) : (
                    <input
                      name={field}
                      value={form[field]}
                      onChange={handleChange}
                      className="form-control"
                      type={field.includes('Num') ? 'number' : 'text'}
                    />
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="manager-body manager-p mb-4">
            <h6 className="fw-bold mb-3">포탈 포틀릿 정보</h6>
            <PortletLayoutSelector
              selectedLayout={selectedLayout}
              setSelectedLayout={setSelectedLayout}
              zoneData={zoneData}
              setZoneData={setZoneData}
            />
          </div>

          <div className="manager-body manager-p mb-4">
            <h6 className="fw-bold mb-3">좌측메뉴정보</h6>
            <MenuListSelector
              selectedMenus={selectedMenus}
              setSelectedMenus={setSelectedMenus}
              menuItems={availableMenus}
            />
          </div>

        </div>
      </div>
    </div>
  );
};

export default ManagerCreate;
